import { useState, useEffect, useCallback, useRef } from 'react'
import { LogOut, Plus, Pencil, Trash2, AlertTriangle, ChevronDown, X, Check, RefreshCw, UserPlus, Search } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import portariaService from '../../services/portariaService'
import leiloesService from '../../services/leiloesService'
import clientesService from '../../services/clientesService'
import NovoClienteModal from '../../components/modals/NovoClienteModal'
import toast from 'react-hot-toast'

function fmtDT(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function nowLocalISO() {
  const now = new Date()
  const pad = n => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}

// ─── Modal de Formulário ─────────────────────────────────────────────────────
function ModalSaida({ leilaoId, orgId, initial, onSaved, onClose }) {
  const emptyForm = {
    leilao_id:       leilaoId,
    comprador_id:    null,
    comprador_nome:  '',
    fazenda:         '',
    fazenda_id:      null,
    qtd_embarcada:   '',
    qtd_machucado:   0,
    obs_machucado:   '',
    motorista:       '',
    placa:           '',
    obs:             '',
    data_hora_saida: nowLocalISO(),
  }

  const [form, setForm]                   = useState(initial?.id
    ? { ...emptyForm, ...initial, fazenda: initial.fazenda || '', data_hora_saida: initial.data_hora_saida ? initial.data_hora_saida.slice(0,16) : nowLocalISO() }
    : emptyForm
  )
  const [comprSearch, setComprSearch]     = useState(initial?.comprador?.nome || initial?.comprador_nome || '')
  const [comprOpcoes, setComprOpcoes]     = useState([])
  const [comprDropOpen, setComprDropOpen] = useState(false)
  const [fazendas, setFazendas]           = useState([])
  const [saving, setSaving]               = useState(false)
  const [showNovoCliente, setShowNovoCliente] = useState(false)
  const searchRef                         = useRef(null)
  const dropRef                           = useRef(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    if (comprSearch.length < 2) { setComprOpcoes([]); setComprDropOpen(false); return }
    const t = setTimeout(async () => {
      try {
        const data = await clientesService.listarClientes(orgId, 1, 30, comprSearch)
        setComprOpcoes(data.data || [])
        setComprDropOpen(true)
      } catch { setComprOpcoes([]) }
    }, 250)
    return () => clearTimeout(t)
  }, [comprSearch, orgId])

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setComprDropOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (!form.comprador_id) { setFazendas([]); return }
    clientesService.getById(orgId, form.comprador_id)
      .then(c => {
        const faz = c.fazendas || []
        setFazendas(faz)
        if (faz.length === 1 && !form.fazenda_id) {
          set('fazenda_id', faz[0].id)
          set('fazenda', faz[0].nome)
        }
      })
      .catch(() => setFazendas([]))
  }, [form.comprador_id])

  const selecionarComprador = (c) => {
    set('comprador_id', c.id)
    set('comprador_nome', c.nome)
    setComprSearch(c.nome)
    setComprDropOpen(false)
    setComprOpcoes([])
  }

  const limparComprador = () => {
    set('comprador_id', null)
    set('comprador_nome', '')
    set('fazenda_id', null)
    set('fazenda', '')
    setComprSearch('')
    setFazendas([])
    setTimeout(() => searchRef.current?.focus(), 50)
  }

  const handleClienteCriado = (c) => {
    selecionarComprador(c)
    if (c.fazendas?.length) {
      setFazendas(c.fazendas)
      set('fazenda_id', c.fazendas[0].id)
      set('fazenda', c.fazendas[0].nome)
    }
    setShowNovoCliente(false)
  }

  const handleFazendaChange = (fazendaId) => {
    const f = fazendas.find(x => String(x.id) === String(fazendaId))
    set('fazenda_id', fazendaId ? Number(fazendaId) : null)
    set('fazenda', f ? f.nome : '')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.qtd_embarcada || parseInt(form.qtd_embarcada) <= 0) {
      toast.error('Informe a quantidade embarcada')
      return
    }
    setSaving(true)
    try {
      const payload = {
        leilao_id:      parseInt(leilaoId),
        comprador_id:   form.comprador_id || null,
        comprador_nome: form.comprador_id ? null : (form.comprador_nome || null),
        fazenda:        form.fazenda || null,
        qtd_embarcada:  parseInt(form.qtd_embarcada),
        qtd_machucado:  parseInt(form.qtd_machucado) || 0,
        obs_machucado:  form.obs_machucado || null,
        motorista:      form.motorista || null,
        placa:          form.placa || null,
        obs:            form.obs || null,
        data_hora_saida: form.data_hora_saida || null,
      }
      if (initial?.id) {
        await portariaService.atualizarSaida(orgId, initial.id, payload)
        toast.success('Saída atualizada')
      } else {
        await portariaService.criarSaida(orgId, payload)
        toast.success('Saída registrada')
      }
      onSaved()
    } catch (err) {
      const detail = err.response?.data?.detail
      toast.error(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : (detail || 'Erro ao salvar'))
    } finally {
      setSaving(false)
    }
  }

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1'
  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-orange-100 rounded-lg">
                <LogOut size={18} className="text-orange-700" />
              </div>
              <h2 className="text-base font-semibold text-gray-800">
                {initial?.id ? 'Editar saída' : 'Nova saída'}
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

            {/* Comprador */}
            <div ref={dropRef} className="relative">
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls + ' mb-0'}>Comprador</label>
                {!form.comprador_id && (
                  <button type="button" onClick={() => setShowNovoCliente(true)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                    <UserPlus size={11} /> Cadastrar novo
                  </button>
                )}
              </div>
              {form.comprador_id ? (
                <div className="flex items-center gap-2 border border-green-300 bg-green-50 rounded-lg px-3 py-2">
                  <Check size={14} className="text-green-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-green-800 flex-1">{comprSearch}</span>
                  <button type="button" onClick={limparComprador}
                    className="text-green-500 hover:text-red-500 transition-colors">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    ref={searchRef}
                    className={inputCls + ' pl-8'}
                    placeholder="Buscar por nome..."
                    value={comprSearch}
                    onChange={e => { setComprSearch(e.target.value); set('comprador_nome', e.target.value) }}
                    autoComplete="off"
                    autoFocus
                  />
                </div>
              )}

              {comprDropOpen && comprOpcoes.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
                  {comprOpcoes.map(c => (
                    <button key={c.id} type="button" onMouseDown={() => selecionarComprador(c)}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors">
                      <div className="font-medium text-gray-900">{c.nome}</div>
                      {c.fazendas?.length > 0 && (
                        <div className="text-xs text-gray-500 mt-0.5">{c.fazendas.map(f => f.nome).join(', ')}</div>
                      )}
                    </button>
                  ))}
                  <button type="button" onMouseDown={() => { setShowNovoCliente(true); setComprDropOpen(false) }}
                    className="w-full text-left px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 font-medium border-t border-blue-100">
                    <UserPlus size={13} /> Cadastrar "{comprSearch}"
                  </button>
                </div>
              )}
            </div>

            {/* Fazenda */}
            <div>
              <label className={labelCls}>Fazenda de destino</label>
              {form.comprador_id && fazendas.length > 0 ? (
                <select
                  value={form.fazenda_id || ''}
                  onChange={e => handleFazendaChange(e.target.value)}
                  className={inputCls}>
                  <option value="">Selecione a fazenda...</option>
                  {fazendas.map(f => (
                    <option key={f.id} value={f.id}>{f.nome}{f.cidade ? ` — ${f.cidade}` : ''}</option>
                  ))}
                </select>
              ) : (
                <input
                  className={inputCls}
                  value={form.fazenda}
                  onChange={e => set('fazenda', e.target.value)}
                  placeholder="Nome da fazenda de destino"
                />
              )}
            </div>

            {/* Qtd embarcada + machucado */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Qtd embarcada <span className="text-red-500">*</span></label>
                <input type="number" min="1" className={inputCls} value={form.qtd_embarcada}
                  onChange={e => set('qtd_embarcada', e.target.value)} placeholder="0" required />
              </div>
              <div>
                <label className={labelCls}>Qtd machucado</label>
                <input type="number" min="0" className={inputCls} value={form.qtd_machucado}
                  onChange={e => set('qtd_machucado', e.target.value)} placeholder="0" />
              </div>
            </div>

            {/* Obs machucado */}
            {parseInt(form.qtd_machucado) > 0 && (
              <div>
                <label className={labelCls + ' text-amber-700'}>
                  <AlertTriangle size={12} className="inline mr-1" />Obs. machucado
                </label>
                <textarea rows={2} className={inputCls + ' resize-none'} value={form.obs_machucado}
                  onChange={e => set('obs_machucado', e.target.value)}
                  placeholder="Descreva os animais machucados..." />
              </div>
            )}

            {/* Motorista + Placa */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Motorista</label>
                <input className={inputCls} value={form.motorista}
                  onChange={e => set('motorista', e.target.value)} placeholder="Nome do motorista" />
              </div>
              <div>
                <label className={labelCls}>Placa</label>
                <input className={inputCls} value={form.placa}
                  onChange={e => set('placa', e.target.value.toUpperCase())} placeholder="ABC-1234" />
              </div>
            </div>

            {/* Data/hora */}
            <div>
              <label className={labelCls}>Data/hora de saída</label>
              <input type="datetime-local" className={inputCls} value={form.data_hora_saida}
                onChange={e => set('data_hora_saida', e.target.value)} />
            </div>

            {/* Obs geral */}
            <div>
              <label className={labelCls}>Observações</label>
              <textarea rows={2} className={inputCls + ' resize-none'} value={form.obs}
                onChange={e => set('obs', e.target.value)} placeholder="Observações gerais..." />
            </div>
          </form>

          {/* Footer */}
          <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium">
              Cancelar
            </button>
            <button onClick={handleSubmit} disabled={saving}
              className="flex-1 px-4 py-2.5 text-sm bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white rounded-xl font-medium transition-colors">
              {saving ? 'Salvando...' : initial?.id ? 'Atualizar' : 'Registrar saída'}
            </button>
          </div>
        </div>
      </div>

      {showNovoCliente && (
        <NovoClienteModal
          isOpen
          onClose={() => setShowNovoCliente(false)}
          onClienteCreated={handleClienteCriado}
          organizadorId={orgId}
          defaultGrupo="comprador"
          title="Novo Comprador"
        />
      )}
    </>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function SaidaAnimais() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes,  setLeiloes]  = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [saidas,   setSaidas]   = useState([])
  const [pendentes, setPendentes] = useState(null)
  const [loading,  setLoading]  = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [modal,    setModal]    = useState(null)
  const [showPendentes, setShowPendentes] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .catch(console.error)
      .finally(() => setLoadingL(false))
  }, [orgId])

  const carregar = useCallback(async () => {
    if (!orgId || !leilaoId) return
    setLoading(true)
    try {
      const [saidasData, pendentesData] = await Promise.all([
        portariaService.listarSaidas(orgId, leilaoId),
        portariaService.pendentesSaida(orgId, leilaoId),
      ])
      setSaidas(saidasData || [])
      setPendentes(pendentesData)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [orgId, leilaoId])

  useEffect(() => { carregar() }, [carregar])

  const handleSaved = () => {
    setModal(null)
    carregar()
  }

  const handleExcluir = async (id) => {
    if (!window.confirm('Excluir esta saída?')) return
    try {
      await portariaService.excluirSaida(orgId, id)
      toast.success('Saída excluída')
      carregar()
    } catch { toast.error('Erro ao excluir') }
  }

  return (
    <div className="space-y-4">

      {/* Título */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LogOut className="w-5 h-5 text-orange-600" />
          <h1 className="text-lg font-semibold text-gray-800">Saída de Animais</h1>
        </div>
        <button onClick={() => setModal('novo')}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium shadow-sm">
          <Plus size={15} /> Nova saída
        </button>
      </div>

      {/* Seletor de leilão */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <label className="text-xs text-gray-500 font-medium">Leilão</label>
            <div className="relative">
              <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)} disabled={loadingL}
                className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                {loadingL
                  ? <option>Carregando...</option>
                  : leiloes.map(l => (
                      <option key={l.id} value={String(l.id)}>
                        {l.nome} — {new Date(l.data_leilao).toLocaleDateString('pt-BR')}
                      </option>
                    ))
                }
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <button onClick={carregar} disabled={loading}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors self-end">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Painel de Pendentes de Embarque */}
      {pendentes && !loading && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowPendentes(!showPendentes)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <AlertTriangle size={14} className="text-blue-700" />
              </div>
              <span className="text-sm font-semibold text-gray-800">Pendentes de Embarque</span>
              {pendentes.totais?.pendente_geral > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold bg-orange-100 text-orange-700 rounded-full">
                  {pendentes.totais.pendente_geral} cab
                </span>
              )}
            </div>
            <ChevronDown size={16} className={`text-gray-400 transition-transform ${showPendentes ? 'rotate-180' : ''}`} />
          </button>

          {showPendentes && (
            <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">

              {/* Resumo */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                <div className="bg-green-50 rounded-lg p-2">
                  <div className="text-xs text-green-600 font-medium">Entrada</div>
                  <div className="text-lg font-bold text-green-800">{pendentes.totais?.entrada || 0}</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2">
                  <div className="text-xs text-blue-600 font-medium">Faturado</div>
                  <div className="text-lg font-bold text-blue-800">{pendentes.totais?.total_faturado || 0}</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-2">
                  <div className="text-xs text-orange-600 font-medium">Embarcado</div>
                  <div className="text-lg font-bold text-orange-800">{pendentes.totais?.total_embarcado || 0}</div>
                </div>
                <div className="bg-red-50 rounded-lg p-2">
                  <div className="text-xs text-red-600 font-medium">Pendente</div>
                  <div className="text-lg font-bold text-red-800">{pendentes.totais?.pendente_geral || 0}</div>
                </div>
              </div>

              {/* Compradores pendentes */}
              {pendentes.compradores?.filter(c => c.pendente > 0).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    🟢 Vendas — Aguardando embarque
                  </h4>
                  <div className="space-y-1">
                    {pendentes.compradores.filter(c => c.pendente > 0).map(c => (
                      <div key={c.comprador_id} className="flex flex-wrap items-center justify-between gap-2 bg-green-50 rounded-lg px-3 py-2">
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-gray-800 block truncate">{c.nome}</span>
                          <span className="text-xs text-gray-500">
                            Fat: {c.qtd_faturada} | Emb: {c.qtd_embarcada}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-bold text-orange-700">{c.pendente} cab</span>
                          <button
                            onClick={() => setModal({ prefill: { comprador_id: c.comprador_id, comprador_nome: c.nome, qtd_embarcada: c.pendente } })}
                            className="px-2 py-1 text-xs bg-orange-600 hover:bg-orange-700 text-white rounded-md font-medium whitespace-nowrap"
                          >
                            Registrar saída
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Defesa pendente */}
              {pendentes.defesa?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    🔴 Defesa — Devolver ao vendedor
                  </h4>
                  <div className="space-y-1">
                    {pendentes.defesa.map(d => (
                      <div key={d.vendedor_id} className="flex flex-wrap items-center justify-between gap-2 bg-red-50 rounded-lg px-3 py-2">
                        <span className="text-sm font-medium text-gray-800 truncate min-w-0">{d.nome}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-bold text-red-700">{d.qtd_defesa} cab</span>
                          <button
                            onClick={() => setModal({ prefill: { comprador_id: d.vendedor_id, comprador_nome: d.nome, qtd_embarcada: d.qtd_defesa } })}
                            className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md font-medium whitespace-nowrap"
                          >
                            Registrar saída
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Não movimentados */}
              {pendentes.nao_movimentados?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    🟡 Não Movimentados — Devolver ao vendedor
                  </h4>
                  <div className="space-y-1">
                    {pendentes.nao_movimentados.map(n => (
                      <div key={n.vendedor_id} className="flex flex-wrap items-center justify-between gap-2 bg-amber-50 rounded-lg px-3 py-2">
                        <span className="text-sm font-medium text-gray-800 truncate min-w-0">{n.nome}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-bold text-amber-700">{n.qtd_nao_mov} cab</span>
                          <button
                            onClick={() => setModal({ prefill: { comprador_id: n.vendedor_id, comprador_nome: n.nome, qtd_embarcada: n.qtd_nao_mov } })}
                            className="px-2 py-1 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded-md font-medium whitespace-nowrap"
                          >
                            Registrar saída
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sem lote */}
              {pendentes.sem_lote > 0 && (
                <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center justify-between">
                  <span className="text-sm text-gray-600">⚪ Animais sem lote (não catalogados)</span>
                  <span className="text-sm font-bold text-gray-700">{pendentes.sem_lote} cab</span>
                </div>
              )}

              {/* Tudo embarcado */}
              {pendentes.totais?.pendente_geral <= 0 && (
                <div className="bg-green-50 rounded-lg px-3 py-3 text-center">
                  <Check size={20} className="text-green-600 mx-auto mb-1" />
                  <p className="text-sm font-medium text-green-800">Todos os animais foram embarcados!</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
        </div>
      ) : saidas.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <LogOut size={36} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Nenhuma saída registrada.</p>
          <button onClick={() => setModal('novo')}
            className="mt-3 text-sm text-orange-600 hover:text-orange-800 font-medium">
            + Registrar primeira saída
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-gray-100">
            {saidas.map(s => (
              <div key={s.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">
                      {s.comprador?.nome || s.comprador_nome || <span className="text-gray-400 italic">Sem comprador</span>}
                    </div>
                    {s.fazenda && <div className="text-xs text-gray-500 truncate">{s.fazenda}</div>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setModal(s)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleExcluir(s.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="font-bold text-gray-900">{s.qtd_embarcada} cab</span>
                  {s.qtd_machucado > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      <AlertTriangle size={10} /> {s.qtd_machucado} mach.
                    </span>
                  )}
                  {(s.motorista || s.placa) && (
                    <span className="text-xs text-gray-500">
                      {[s.motorista, s.placa].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">{fmtDT(s.data_hora_saida)}</span>
                </div>
              </div>
            ))}
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between text-xs">
              <span className="text-gray-500 font-medium">{saidas.length} registro(s)</span>
              <span className="font-bold text-gray-800">{saidas.reduce((s, e) => s + (e.qtd_embarcada || 0), 0)} cab total</span>
            </div>
          </div>
          {/* Desktop table */}
          <table className="hidden sm:table w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase">Comprador / Fazenda</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600 text-xs uppercase">Embarcado</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600 text-xs uppercase">Machucado</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase">Motorista / Placa</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase">Saída</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {saidas.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{s.comprador?.nome || s.comprador_nome || <span className="text-gray-400 italic">Sem comprador</span>}</div>
                    {s.fazenda && <div className="text-xs text-gray-500">{s.fazenda}</div>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-bold text-gray-900">{s.qtd_embarcada}</span>
                    <span className="text-xs text-gray-500 ml-1">cab</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {s.qtd_machucado > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <AlertTriangle size={10} /> {s.qtd_machucado}
                      </span>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-800">{s.motorista || <span className="text-gray-400">—</span>}</div>
                    {s.placa && <div className="text-xs text-gray-500 font-mono">{s.placa}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{fmtDT(s.data_hora_saida)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setModal(s)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => handleExcluir(s.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td className="px-4 py-2 text-xs text-gray-500 font-medium">{saidas.length} registro(s)</td>
                <td className="px-4 py-2 text-center font-bold text-gray-800 text-sm">
                  {saidas.reduce((s, e) => s + (e.qtd_embarcada || 0), 0)} cab
                </td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <ModalSaida
          leilaoId={leilaoId}
          orgId={orgId}
          initial={modal === 'novo' ? null : modal.prefill ? { comprador_id: modal.prefill.comprador_id, comprador_nome: modal.prefill.comprador_nome, comprador: { nome: modal.prefill.comprador_nome }, qtd_embarcada: modal.prefill.qtd_embarcada } : modal}
          onSaved={handleSaved}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}
